@let it = item();
@let d = detail();
<header class="doc-head">
  @if (it) {
    <i class="doc-head-icon" [class]="icon()" aria-hidden="true"></i>
    @if (renaming()) {
      <label class="doc-rename">
        <span class="visually-hidden">{{ 'documents.actions.rename' | translate }}</span>
        <input
          class="p-inputtext doc-input doc-rename-input"
          [value]="renameBase()"
          (input)="renameBase.set($any($event.target).value)"
          (keydown)="onRenameKey($event)"
          (blur)="commitRename()" />
        <span class="doc-muted">{{ it.ext ? '.' + it.ext : '' }}</span>
      </label>
    } @else {
      <h2 class="doc-title" [title]="it.fileName">{{ it.fileName }}</h2>
      @if (it.editable) {
        <button type="button" class="doc-icon-btn" (click)="startRename()" [attr.aria-label]="'documents.actions.rename' | translate" [title]="('documents.actions.rename' | translate) + ' (F2)'">
          <i class="fa-solid fa-pen" aria-hidden="true"></i>
        </button>
      }
    }
  }
  <button type="button" class="doc-icon-btn doc-close" (click)="closed.emit()" [attr.aria-label]="'documents.actions.close' | translate" [title]="'documents.actions.close' | translate">
    <i class="fa-solid fa-xmark" aria-hidden="true"></i>
  </button>
</header>

@if (it) {
  <div class="doc-actions" role="toolbar" [attr.aria-label]="'documents.detail.actions' | translate">
    @if (it.link) {
      <a class="p-button p-button-primary doc-btn" [href]="it.link" target="_blank" rel="noopener noreferrer">
        <i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>{{ 'documents.actions.openLink' | translate }}
      </a>
    } @else {
      <button type="button" class="p-button p-button-primary doc-btn" [disabled]="busy()" (click)="download.emit()">
        <i class="fa-solid fa-download" aria-hidden="true"></i>{{ 'documents.actions.download' | translate }}
      </button>
      <button type="button" class="p-button p-button-secondary doc-btn doc-btn--icon" [disabled]="busy()" (click)="openTab.emit()" [attr.aria-label]="'documents.actions.openTab' | translate" [title]="'documents.actions.openTab' | translate">
        <i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>
      </button>
      <button type="button" class="p-button p-button-secondary doc-btn doc-btn--icon" [disabled]="busy()" (click)="copyLink.emit()" [attr.aria-label]="'documents.actions.copyLink' | translate" [title]="'documents.actions.copyLinkHelp' | translate">
        <i class="fa-solid fa-link" aria-hidden="true"></i>
      </button>
    }
    <button
      type="button"
      class="p-button p-button-secondary doc-btn doc-btn--icon"
      [class.doc-fav--on]="it.favourite"
      [attr.aria-pressed]="it.favourite"
      (click)="favourite.emit(!it.favourite)"
      [attr.aria-label]="(it.favourite ? 'documents.actions.unfavourite' : 'documents.actions.favourite') | translate"
      [title]="(it.favourite ? 'documents.actions.unfavourite' : 'documents.actions.favourite') | translate">
      <i class="fa-star" [class.fa-solid]="it.favourite" [class.fa-regular]="!it.favourite" aria-hidden="true"></i>
    </button>
    <span class="doc-spacer"></span>
    @if (it.editable) {
      @if (it.trashed) {
        <button type="button" class="p-button p-button-secondary doc-btn" [disabled]="busy()" (click)="restore.emit()">
          <i class="fa-solid fa-rotate-left" aria-hidden="true"></i>{{ 'documents.actions.restore' | translate }}
        </button>
        <button type="button" class="p-button p-button-alert doc-btn" [disabled]="busy()" (click)="askPurge()">
          <i class="fa-solid fa-trash" aria-hidden="true"></i>{{ (confirmPurge() ? 'documents.actions.purgeConfirm' : 'documents.actions.purge') | translate }}
        </button>
      } @else {
        <button type="button" class="p-button p-button-secondary doc-btn doc-btn--icon" [disabled]="busy()" (click)="trash.emit()" [attr.aria-label]="'documents.actions.trash' | translate" [title]="('documents.actions.trash' | translate) + ' (Del)'">
          <i class="fa-solid fa-trash-can" aria-hidden="true"></i>
        </button>
      }
    }
  </div>

  <div class="doc-body">
    @if (it.trashed) {
      <p class="doc-note doc-note--warn">
        <i class="fa-solid fa-trash-can" aria-hidden="true"></i>
        {{ 'documents.detail.inBin' | translate: { date: fmt().dateTime(it.trashedAt) } }}
      </p>
    }
    @if (it.archived) {
      <p class="doc-note">
        <i class="fa-solid fa-lock" aria-hidden="true"></i>{{ 'documents.detail.archived' | translate }}
      </p>
    }

    <!-- preview (re-created per document) -->
    @if (!it.link) {
      <section class="doc-preview" [attr.aria-label]="'documents.detail.preview' | translate">
        <iu-document-preview [docId]="it.docId" [fileName]="it.fileName" [size]="it.size" [version]="it.version" (download)="download.emit()" />
      </section>
    }

    <dl class="doc-meta">
      <div>
        <dt>{{ 'documents.columns.type' | translate }}</dt>
        <dd>{{ typeLabel(it.type) }}</dd>
      </div>
      <div>
        <dt>{{ 'documents.columns.size' | translate }}</dt>
        <dd>{{ size(it.size) || '-' }}</dd>
      </div>
      <div>
        <dt>{{ 'documents.columns.created' | translate }}</dt>
        <dd [title]="fmt().exact(it.created)">{{ fmt().dateTime(it.created) || '-' }}</dd>
      </div>
      <div>
        <dt>{{ 'documents.columns.updated' | translate }}</dt>
        <dd [title]="fmt().exact(it.updated)">{{ fmt().dateTime(it.updated) || '-' }}</dd>
      </div>
      <div>
        <dt>{{ 'documents.columns.uploadedBy' | translate }}</dt>
        <dd>{{ it.uploadedBy || '-' }}</dd>
      </div>
      @if (it.version > 1) {
        <div>
          <dt>{{ 'documents.versions.current' | translate }}</dt>
          <dd>v{{ it.version }}</dd>
        </div>
      }
      <div class="doc-meta-wide">
        <dt>{{ 'documents.columns.record' | translate }}</dt>
        <dd>
          @if (!it.tableId) {
            <span class="doc-muted">-</span>
          }
          @if (it.editable && it.type === 'DOCUMENT' && !it.trashed) {
            <button type="button" class="doc-link-btn" (click)="linkRecord.emit(it)">
              <i class="fa-solid fa-link" aria-hidden="true"></i>{{ (it.tableId ? 'documents.link.change' : 'documents.link.title') | translate }}
            </button>
          }
        </dd>
      </div>
      @if (it.tableId) {
        <div class="doc-meta-wide">
          <dt class="visually-hidden">{{ 'documents.columns.record' | translate }}</dt>
          <dd>
            <span>{{ it.tableLabel || it.tableName }}@if (it.recordLabel || it.recordId) { - {{ it.recordLabel || '#' + it.recordId }}}</span>
            @if (it.recordId && it.tableName) {
              <button type="button" class="doc-link-btn" (click)="openRecord.emit(it)">
                <i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i>{{ 'documents.actions.openRecord' | translate }}
              </button>
              <button type="button" class="doc-link-btn" (click)="showRecord.emit(it)">
                <i class="fa-solid fa-folder-open" aria-hidden="true"></i>{{ 'documents.actions.recordDocuments' | translate }}
              </button>
            }
          </dd>
        </div>
      }
    </dl>

    @if (loading()) {
      <p class="doc-state"><i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>{{ 'documents.state.loading' | translate }}</p>
    } @else if (error()) {
      <p class="doc-state doc-state--error">
        <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>{{ error() }}
        <button type="button" class="doc-link-btn" (click)="retry.emit()">{{ 'documents.actions.retry' | translate }}</button>
      </p>
    } @else if (d) {
      <!-- text extraction -->
      <section class="doc-section">
        <h3>{{ 'documents.ocr.title' | translate }}</h3>
        <p class="doc-ocr">
          <span class="doc-ocr-status" [class]="'doc-ocr-status--' + (d.ocrStatus ?? 'UNKNOWN')">
            <i class="fa-solid" [class]="d.ocrStatus === 'PENDING' ? 'fa-circle-notch fa-spin' : d.ocrStatus === 'DONE' ? 'fa-check' : d.ocrStatus === 'FAILED' ? 'fa-triangle-exclamation' : 'fa-minus'" aria-hidden="true"></i>
            {{ 'documents.ocr.' + (d.ocrStatus ?? 'UNKNOWN') | translate }}
          </span>
          @if (d.ocrDate) {
            <span class="doc-muted" [title]="fmt().exact(d.ocrDate)">{{ fmt().dateTime(d.ocrDate) }}</span>
          }
          @if (canRetryOcr() && d.ocrStatus !== 'PENDING') {
            <button type="button" class="doc-link-btn" [disabled]="busy()" (click)="retryOcr.emit()">
              <i class="fa-solid fa-rotate-right" aria-hidden="true"></i>{{ 'documents.ocr.retry' | translate }}
            </button>
          }
        </p>
      </section>

      <!-- versions -->
      @if (!it.link && (it.editable || d.versions.length)) {
        <section class="doc-section">
          <h3>{{ 'documents.versions.title' | translate }} <span class="doc-count">{{ it.version }}</span></h3>
          @if (it.editable && !it.trashed) {
            @if (versionProgress() !== null) {
              <p class="doc-muted"><i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>{{ 'documents.versions.sending' | translate: { progress: versionProgress() } }}</p>
            } @else {
              <button type="button" class="p-button p-button-secondary doc-btn" [disabled]="busy()" (click)="versionInput.click()">
                <i class="fa-solid fa-file-arrow-up" aria-hidden="true"></i>{{ 'documents.versions.upload' | translate }}
              </button>
              <input #versionInput type="file" hidden (change)="onVersionPicked(versionInput)" />
            }
          }
          @if (d.versions.length) {
            <ol class="doc-versions" reversed>
              <li class="doc-version doc-version--current">
                <span class="doc-version-no">v{{ it.version }}</span>
                <span class="doc-version-main">{{ it.fileName }} <span class="doc-muted">{{ size(it.size) }} - {{ 'documents.versions.currentShort' | translate }}</span></span>
              </li>
              @for (v of d.versions.slice().reverse(); track v.version) {
                <li class="doc-version">
                  <span class="doc-version-no">v{{ v.version }}</span>
                  <span class="doc-version-main">
                    {{ v.fileName }}
                    <span class="doc-muted">{{ size(v.size) }} - {{ fmt().dateTime(v.date) }}@if (v.by) { - {{ v.by }}}</span>
                  </span>
                  <button type="button" class="doc-icon-btn" (click)="downloadVersion.emit(v)" [attr.aria-label]="'documents.actions.download' | translate" [title]="'documents.actions.download' | translate">
                    <i class="fa-solid fa-download" aria-hidden="true"></i>
                  </button>
                  @if (it.editable && !it.trashed) {
                    <button type="button" class="doc-link-btn" [disabled]="busy()" (click)="askRestore(v)">
                      {{ (confirmRestore() === v.version ? 'documents.versions.restoreConfirm' : 'documents.versions.restore') | translate }}
                    </button>
                  }
                </li>
              }
            </ol>
          }
        </section>
      }

      @if (d.matches.length) {
        <section class="doc-section">
          <h3>{{ 'documents.detail.matches' | translate }} <span class="doc-count">{{ d.matches.length }}</span></h3>
          <ul class="doc-matches">
            @for (m of d.matches; track $index) {
              <li [innerHTML]="highlight(m)"></li>
            }
          </ul>
        </section>
      }

      @if (d.text) {
        <section class="doc-section">
          <button type="button" class="doc-section-toggle" [attr.aria-expanded]="showText()" (click)="showText.set(!showText())">
            <i class="fa-solid" [class]="showText() ? 'fa-chevron-down' : 'fa-chevron-right'" aria-hidden="true"></i>{{ 'documents.detail.text' | translate }}
          </button>
          @if (showText()) {
            <pre class="doc-text">{{ d.text }}</pre>
            @if (d.textTruncated) {
              <p class="doc-muted">{{ 'documents.detail.textTruncated' | translate }}</p>
            }
          }
        </section>
      }

      <!-- technical data (not tags): fields of the document server -->
      <section class="doc-section">
        <button type="button" class="doc-section-toggle" [attr.aria-expanded]="showTechnical()" (click)="showTechnical.set(!showTechnical())">
          <i class="fa-solid" [class]="showTechnical() ? 'fa-chevron-down' : 'fa-chevron-right'" aria-hidden="true"></i>{{ 'documents.detail.technical' | translate }}
        </button>
        @if (showTechnical()) {
          <table class="doc-kv">
            <tbody>
              @for (t of d.technical; track t.key) {
                <tr>
                  <th scope="row">{{ 'documents.technical.' + t.key | translate }}</th>
                  <td [title]="t.value ?? ''">{{ techValue(t) }}</td>
                </tr>
              }
            </tbody>
          </table>
        }
      </section>

      <!-- tags: names without their internal prefix; the user tags can be changed, added, removed -->
      <section class="doc-section">
        <h3>{{ 'documents.detail.tags' | translate }} <span class="doc-count">{{ visibleRows().length }}</span></h3>
        <form class="doc-tag-list" (submit)="$event.preventDefault(); commitTags()">
          @for (r of rows(); track r.id) {
            @if (!r.removed) {
              <div class="doc-tag-row" [class.doc-tag-row--ro]="!r.editable">
                @if (r.isNew) {
                  <input
                    class="p-inputtext doc-input doc-tag-name"
                    list="doc-tag-names"
                    maxlength="60"
                    [placeholder]="'documents.tagEditor.name' | translate"
                    [attr.aria-label]="'documents.tagEditor.name' | translate"
                    [attr.aria-invalid]="badRow(r) ? 'true' : null"
                    [attr.aria-describedby]="badRow(r) ? 'doc-tag-error' : null"
                    [value]="r.name"
                    (keydown)="onTagKey($event, true)"
                    (input)="setRow(r.id, { name: $any($event.target).value })" />
                } @else {
                  <span class="doc-tag-label" [title]="r.name">{{ rowLabel(r) }}</span>
                }
                @if (r.editable) {
                  <input
                    class="p-inputtext doc-input doc-tag-value"
                    maxlength="1000"
                    [placeholder]="'documents.tagEditor.value' | translate"
                    [attr.aria-label]="rowLabel(r)"
                    [value]="r.value"
                    (keydown)="onTagKey($event, false)"
                    (input)="setRow(r.id, { value: $any($event.target).value })" />
                  <button type="button" class="doc-icon-btn" (click)="removeRow(r.id)" [attr.aria-label]="'documents.tagEditor.remove' | translate" [title]="'documents.tagEditor.remove' | translate">
                    <i class="fa-solid fa-xmark" aria-hidden="true"></i>
                  </button>
                } @else {
                  <span class="doc-tag-value doc-tag-value--ro">{{ r.value }}</span>
                  <button type="button" class="doc-icon-btn" (click)="filterTag.emit({ key: r.name, value: r.value })" [attr.aria-label]="'documents.filters.byTag' | translate" [title]="'documents.filters.byTag' | translate">
                    <i class="fa-solid fa-filter" aria-hidden="true"></i>
                  </button>
                }
              </div>
            }
          } @empty {
            <p class="doc-muted">{{ 'documents.tagEditor.none' | translate }}</p>
          }
          @if (rowError()) {
            <p id="doc-tag-error" class="doc-state doc-state--error" role="alert">{{ rowError()! | translate }}</p>
          }
          <div class="doc-tag-actions">
            @if (it.editable && !it.trashed) {
              <button type="button" class="doc-link-btn" (click)="addRow()">
                <i class="fa-solid fa-plus" aria-hidden="true"></i>{{ 'documents.tagEditor.add' | translate }}
              </button>
            }
            @if (dirty()) {
              <span class="doc-spacer"></span>
              <button type="submit" class="p-button p-button-primary doc-btn" [disabled]="busy() || !!rowError()">{{ 'documents.actions.save' | translate }}</button>
              <button type="button" class="p-button p-button-secondary doc-btn" (click)="resetRows()">{{ 'documents.actions.cancel' | translate }}</button>
            }
          </div>
          <datalist id="doc-tag-names">
            @for (k of tagNames(); track k.key) {
              @if (k.editable) {
                <option [value]="k.key">{{ k.label }}</option>
              }
            }
          </datalist>
        </form>
      </section>

      <!-- access log: who viewed or downloaded it (administrators, the user who added it) -->
      @if (d.canSeeAccess) {
        <section class="doc-section">
          <button type="button" class="doc-section-toggle" [attr.aria-expanded]="showAccess()" aria-controls="doc-access" (click)="toggleAccess()">
            <i class="fa-solid" [class]="showAccess() ? 'fa-chevron-down' : 'fa-chevron-right'" aria-hidden="true"></i>{{ 'documents.access.title' | translate }}
            @if (access(); as rows) {
              <span class="doc-count">{{ rows.length }}</span>
            }
          </button>
          @if (showAccess()) {
            <div id="doc-access" aria-live="polite">
              @if (accessState() === 'loading') {
                <p class="doc-muted"><i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>{{ 'documents.state.loading' | translate }}</p>
              } @else if (accessState() === 'error') {
                <p class="doc-state doc-state--error">
                  <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>{{ 'documents.access.error' | translate }}
                  <button type="button" class="doc-link-btn" (click)="loadAccess()">{{ 'documents.actions.retry' | translate }}</button>
                </p>
              } @else {
                <ul class="doc-access">
                  @for (a of access() ?? []; track a.id) {
                    <li class="doc-access-row">
                      <i class="fa-solid doc-access-icon" [class]="a.action === 'DOWNLOAD' ? 'fa-download' : 'fa-eye'" aria-hidden="true"></i>
                      <span class="doc-access-main">
                        <strong>{{ a.userName || '#' + a.userId }}</strong>
                        <span>{{ accessLabel(a) }}</span>
                        @if (a.count > 1) {
                          <span class="doc-muted">{{ 'documents.access.times' | translate: { count: a.count } }}</span>
                        }
                      </span>
                      <span class="doc-muted doc-access-date" [title]="fmt().exact(a.last)">{{ fmt().dateTime(a.last) }}</span>
                    </li>
                  } @empty {
                    <li class="doc-muted">{{ 'documents.access.none' | translate }}</li>
                  }
                </ul>
                <p class="doc-muted doc-access-note">{{ 'documents.access.note' | translate }}</p>
              }
            </div>
          }
        </section>
      }
    }
  </div>
}
